import { useEffect, useRef } from 'react'
import { Avatar } from './Avatar.tsx'
import { useCall } from '../store/call.ts'
import { useChats } from '../store/chats.ts'
import { useAuth } from '../store/auth.ts'

/**
 * ЭКРАН ЗВОНКА
 *
 * Занимает весь экран и перекрывает всё остальное. Это не грубость:
 * во время звонка человек делает ровно одно дело, и любая другая
 * кнопка на экране — это кнопка, которую нажмут случайно, прижав
 * телефон к уху.
 *
 * Кнопка «положить» крупнее остальных и стоит отдельно. Промахнуться
 * по ней нельзя, а нажать её случайно — можно, поэтому она внизу,
 * куда палец не попадает сам.
 */

export function CallView() {
  const me = useAuth((s) => s.user)
  const chats = useChats((s) => s.chats)

  const call = useCall((s) => s.call)
  const phase = useCall((s) => s.phase)
  const local = useCall((s) => s.local)
  const remote = useCall((s) => s.remote)
  const muted = useCall((s) => s.muted)
  const cameraOff = useCall((s) => s.cameraOff)
  const error = useCall((s) => s.error)
  const callerName = useCall((s) => s.callerName)
  const chatTitle = useCall((s) => s.chatTitle)

  const accept = useCall((s) => s.accept)
  const hangUp = useCall((s) => s.hangUp)
  const toggleMute = useCall((s) => s.toggleMute)
  const toggleCamera = useCall((s) => s.toggleCamera)

  if (!call || phase === 'idle') return null

  const video = call.kind === 'VIDEO'
  const chat = chats.find((c) => c.id === call.chatId)
  const title = callerName || chatTitle || chat?.title || 'Звонок'

  const status =
    error ??
    (phase === 'ringing-in'
      ? video
        ? 'Видеозвонок'
        : 'Звонит'
      : phase === 'ringing-out'
        ? 'Вызываю…'
        : phase === 'connecting'
          ? 'Соединяю…'
          : phase === 'failed'
            ? 'Связь оборвалась'
            : 'Разговор')

  return (
    <div className={'call' + (video ? ' is-video' : '')}>
      {video && <VideoStage local={local} remote={remote} cameraOff={cameraOff} />}

      <div className="call-who">
        {!video && (
          <Avatar
            id={call.chatId}
            name={title}
            url={chat?.avatarUrl ?? null}
            size={112}
          />
        )}
        <div className="call-name">{title}</div>
        <div className={'call-status' + (error ? ' is-error' : '')}>{status}</div>
        {phase === 'talking' && <Elapsed since={call.startedAt} />}
      </div>

      <div className="call-controls">
        {phase === 'ringing-in' ? (
          <>
            <button className="call-btn is-decline" onClick={() => void hangUp()}>
              <span aria-hidden="true">✕</span>
              <span className="call-btn-name">Отклонить</span>
            </button>
            <button className="call-btn is-accept" onClick={() => void accept()}>
              <span aria-hidden="true">✆</span>
              <span className="call-btn-name">Ответить</span>
            </button>
          </>
        ) : (
          <>
            <button
              className={'call-btn is-small' + (muted ? ' is-on' : '')}
              onClick={toggleMute}
              aria-label={muted ? 'Включить микрофон' : 'Выключить микрофон'}
            >
              <span aria-hidden="true">{muted ? '🔇' : '🎙'}</span>
              <span className="call-btn-name">{muted ? 'Включить' : 'Микрофон'}</span>
            </button>

            {video && (
              <button
                className={'call-btn is-small' + (cameraOff ? ' is-on' : '')}
                onClick={toggleCamera}
                aria-label={cameraOff ? 'Включить камеру' : 'Выключить камеру'}
              >
                <span aria-hidden="true">{cameraOff ? '🚫' : '📹'}</span>
                <span className="call-btn-name">Камера</span>
              </button>
            )}

            <button className="call-btn is-decline" onClick={() => void hangUp()}>
              <span aria-hidden="true">✕</span>
              <span className="call-btn-name">Положить</span>
            </button>
          </>
        )}
      </div>

      {/* Звук собеседника играет всегда, даже когда картинки нет */}
      {Object.entries(remote).map(([userId, stream]) =>
        video ? null : <Audio key={userId} stream={stream} />,
      )}

      {me === null && null}
    </div>
  )
}

/** Сколько идёт разговор: без этого непонятно, соединилось ли вообще. */
function Elapsed({ since }: { since: string }) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const started = new Date(since).getTime()

    const tick = () => {
      if (!ref.current) return
      const seconds = Math.max(0, Math.floor((Date.now() - started) / 1000))
      const minutes = Math.floor(seconds / 60)
      ref.current.textContent = `${minutes}:${String(seconds % 60).padStart(2, '0')}`
    }

    tick()
    const timer = setInterval(tick, 1000)
    return () => clearInterval(timer)
  }, [since])

  return <div className="call-timer" ref={ref} />
}

function Audio({ stream }: { stream: MediaStream }) {
  const ref = useRef<HTMLAudioElement>(null)

  useEffect(() => {
    if (ref.current) {
      ref.current.srcObject = stream
      // Браузер может отказать в звуке, если страницу ещё не трогали;
      // к этому моменту трогали — но проглотить отказ всё равно нужно
      void ref.current.play().catch(() => {})
    }
  }, [stream])

  return <audio ref={ref} autoPlay playsInline />
}

function VideoStage({
  local,
  remote,
  cameraOff,
}: {
  local: MediaStream | null
  remote: Record<string, MediaStream>
  cameraOff: boolean
}) {
  const streams = Object.entries(remote)

  return (
    <div className={'call-stage' + (streams.length > 1 ? ' is-many' : '')}>
      {streams.map(([userId, stream]) => (
        <Video key={userId} stream={stream} className="call-remote" />
      ))}

      {/* Своя картинка маленькая в углу: смотреть на себя незачем,
          но проверить, попадаешь ли в кадр, нужно */}
      {local && !cameraOff && <Video stream={local} className="call-local" muted />}
    </div>
  )
}

function Video({
  stream,
  className,
  muted = false,
}: {
  stream: MediaStream
  className: string
  muted?: boolean
}) {
  const ref = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    if (ref.current) ref.current.srcObject = stream
  }, [stream])

  return <video ref={ref} className={className} autoPlay playsInline muted={muted} />
}
